/*
 * WP Masters: CERE glossary tooltips.
 *
 * Finds glossary terms (e.g. "niet-poreus") in the page text and adds a small
 * info button after each occurrence. Hover, focus or tap opens a tooltip with
 * the explanation. Terms and texts come from the locale files via
 * window.cereGlossary (set in layout/theme.liquid), so every language has its
 * own term pattern and explanation.
 *
 * The tooltip itself is moved to <body> while open and positioned above the
 * "i", so containers with overflow: hidden (accordions, tabs) cannot clip it.
 *
 * Skipped: header, footer, forms, links, buttons, scripts and anything inside
 * [data-cere-glossary-skip].
 */
(function () {
  'use strict';

  var SKIP_SELECTOR = 'script, style, noscript, template, textarea, input, select, button, a, title, header, footer, .header-wrapper, .cere-term, .cere-term__tip, [data-cere-glossary-skip]';
  var GAP = 8;
  var MARGIN = 12;
  var counter = 0;

  function buildRegex(pattern) {
    try {
      return new RegExp('(' + pattern + ')', 'gi');
    } catch (e) {
      return null;
    }
  }

  function createTerm(text, entry) {
    var id = 'cere-term-tip-' + ++counter;
    var wrapper = document.createElement('span');
    wrapper.className = 'cere-term';
    wrapper.appendChild(document.createTextNode(text));

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cere-term__info';
    button.setAttribute('aria-label', entry.label);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-describedby', id);
    button.textContent = 'i';

    var tip = document.createElement('span');
    tip.className = 'cere-term__tip';
    tip.id = id;
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    tip.innerHTML = (entry.title ? '<strong class="cere-term__title">' + entry.title + '</strong>' : '') + entry.body;

    wrapper.appendChild(button);
    wrapper.appendChild(tip);
    wrapper._cereTip = tip;
    tip._cereTerm = wrapper;
    return wrapper;
  }

  function wrapMatches(textNode, regex, entry) {
    var value = textNode.nodeValue;
    regex.lastIndex = 0;
    if (!regex.test(value)) return;

    var fragment = document.createDocumentFragment();
    var lastIndex = 0;
    regex.lastIndex = 0;
    var match;
    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) fragment.appendChild(document.createTextNode(value.slice(lastIndex, match.index)));
      fragment.appendChild(createTerm(match[0], entry));
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < value.length) fragment.appendChild(document.createTextNode(value.slice(lastIndex)));
    textNode.parentNode.replaceChild(fragment, textNode);
  }

  function scan(root) {
    var entries = window.cereGlossary || [];
    entries.forEach(function (entry) {
      if (!entry || !entry.pattern || !entry.body) return;
      var regex = buildRegex(entry.pattern);
      if (!regex) return;

      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          var parent = node.parentElement;
          if (!parent || parent.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) {
        wrapMatches(node, regex, entry);
      });
    });
  }

  function termFrom(target) {
    if (!target || !target.closest) return null;
    var term = target.closest('.cere-term');
    if (term) return term;
    var tip = target.closest('.cere-term__tip');
    return tip ? tip._cereTerm : null;
  }

  function position(term) {
    var tip = term._cereTip;
    var button = term.querySelector('.cere-term__info');
    if (!tip || !button) return;

    var buttonRect = button.getBoundingClientRect();
    var width = tip.offsetWidth;
    var height = tip.offsetHeight;

    // Centered above the "i", kept inside the viewport.
    var left = buttonRect.left + buttonRect.width / 2 - width / 2;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - MARGIN - width));

    var top = buttonRect.top - height - GAP;
    if (top < MARGIN) top = buttonRect.bottom + GAP;

    tip.style.left = left + window.scrollX + 'px';
    tip.style.top = top + window.scrollY + 'px';
  }

  function open(term) {
    var tip = term && term._cereTip;
    var button = term && term.querySelector('.cere-term__info');
    if (!tip || !button || !tip.hidden) return;

    document.querySelectorAll('.cere-term.is-open').forEach(function (other) {
      if (other !== term) close(other);
    });

    if (tip.parentNode !== document.body) document.body.appendChild(tip);
    tip.hidden = false;
    term.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    position(term);
  }

  function close(term) {
    var tip = term && term._cereTip;
    var button = term && term.querySelector('.cere-term__info');
    if (!tip || !button) return;
    tip.hidden = true;
    term.classList.remove('is-open', 'is-pinned');
    button.setAttribute('aria-expanded', 'false');
  }

  function closeAll() {
    document.querySelectorAll('.cere-term.is-open').forEach(close);
  }

  function bindEvents() {
    var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover)').matches;

    if (hoverCapable) {
      document.addEventListener('mouseover', function (event) {
        var term = termFrom(event.target);
        if (term) open(term);
      });
      document.addEventListener('mouseout', function (event) {
        var term = termFrom(event.target);
        if (!term || term.classList.contains('is-pinned')) return;
        if (termFrom(event.relatedTarget) === term) return;
        close(term);
      });
    }

    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('.cere-term__info');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        var term = button.closest('.cere-term');
        if (term.classList.contains('is-pinned')) {
          close(term);
        } else {
          open(term);
          term.classList.add('is-pinned');
        }
        return;
      }
      if (!(event.target.closest && event.target.closest('.cere-term__tip'))) closeAll();
    });

    document.addEventListener('focusin', function (event) {
      if (event.target.classList && event.target.classList.contains('cere-term__info')) open(event.target.closest('.cere-term'));
    });
    document.addEventListener('focusout', function (event) {
      if (event.target.classList && event.target.classList.contains('cere-term__info')) {
        var term = event.target.closest('.cere-term');
        if (!term.classList.contains('is-pinned')) close(term);
      }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
    });

    // Keep open tooltips attached to their "i" while scrolling, resizing or
    // when an accordion closes underneath them.
    var reposition = function () {
      document.querySelectorAll('.cere-term.is-open').forEach(function (term) {
        var button = term.querySelector('.cere-term__info');
        if (!button || !button.getClientRects().length) close(term);
        else position(term);
      });
    };
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('toggle', reposition, true);
  }

  function init() {
    var main = document.getElementById('MainContent') || document.body;
    scan(main);
    bindEvents();
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
